import {
  Button,
  Caption1,
  Checkbox,
  SkeletonItem,
  Switch,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  Archive20Regular,
  Attach16Filled,
  Delete20Regular,
  Flag16Filled,
  Flag20Filled,
  Flag20Regular,
  Mail20Regular,
  MailRead20Regular,
} from '@fluentui/react-icons';
import type { Thread } from '@gandhi/mail-protocol';
import { useBrand } from '@gandhi-group/ui';
import type { ReactElement } from 'react';
import { relativeDate } from '../format.js';
import { emptyFolderMessage } from '../state/folders.js';
import { useController, useMail } from '../state/context.js';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px 6px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  spacer: { flex: 1 },
  bulk: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    background: tokens.colorBrandBackground2,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    minHeight: 0,
  },
  row: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '2px',
    paddingLeft: '8px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    ':hover': { background: tokens.colorNeutralBackground1Hover },
    '&:hover [data-row-actions]': { opacity: 1 },
    '&:focus-within [data-row-actions]': { opacity: 1 },
  },
  active: { background: tokens.colorBrandBackground2 },
  accent: {
    position: 'absolute',
    insetBlock: 0,
    insetInlineStart: 0,
    width: '3px',
  },
  select: { paddingTop: '10px' },
  open: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    padding: '8px 4px 8px 4px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    color: tokens.colorNeutralForeground1,
  },
  line: { display: 'flex', alignItems: 'baseline', gap: '8px' },
  sender: { flex: 1, minWidth: 0, fontWeight: tokens.fontWeightSemibold },
  date: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  subject: { fontSize: tokens.fontSizeBase300 },
  preview: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  unread: { fontWeight: tokens.fontWeightBold },
  indicators: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '10px 2px 0 0',
    color: tokens.colorNeutralForeground3,
  },
  // Revealed on hover, kept in the tab order at all times: a control a keyboard cannot
  // reach is not a control.
  actions: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 4px 0 0',
    opacity: 0,
  },
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '48px 24px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  skeleton: { display: 'flex', flexDirection: 'column', gap: '4px', padding: '12px' },
  skeletonRow: { display: 'flex', flexDirection: 'column', gap: '6px', paddingBottom: '10px' },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

function participantLabel(thread: Thread): string {
  const first = thread.participants[0];
  if (first === undefined) return 'Unknown';
  return first.name ?? first.address;
}

/**
 * Dense rows, one conversation each: who it is from, what it is about, a line of it,
 * and when. Weight and a leading accent bar carry unread, so the state survives being
 * read at a glance rather than needing a legend.
 */
export function MessageList(): ReactElement {
  const styles = useStyles();
  const controller = useController();
  const { accent } = useBrand();
  const threads = useMail((s) => s.threads);
  const total = useMail((s) => s.threadTotal);
  const activeThreadId = useMail((s) => s.activeThreadId);
  const selected = useMail((s) => s.selectedThreadIds);
  const loading = useMail((s) => s.loading);
  const unreadOnly = useMail((s) => s.unreadOnly);
  const flaggedOnly = useMail((s) => s.flaggedOnly);
  const search = useMail((s) => s.search);
  const folders = useMail((s) => s.folders);
  const activeFolderId = useMail((s) => s.activeFolderId);

  const folder = folders.find((f) => f.id === activeFolderId);
  const allSelected = threads.length > 0 && selected.length === threads.length;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Checkbox
          aria-label="Select all conversations"
          checked={allSelected ? true : selected.length > 0 ? 'mixed' : false}
          onChange={(_event, data) => controller.selectAllThreads(data.checked === true)}
        />
        <div className={styles.headerText}>
          <Text weight="semibold">{search === '' ? (folder?.name ?? 'Messages') : 'Search'}</Text>
          <Caption1 data-testid="thread-count">
            {total === 0
              ? 'No conversations'
              : `Showing ${threads.length} of ${total} conversation${total === 1 ? '' : 's'}`}
          </Caption1>
        </div>
        <span className={styles.spacer} />
        <Switch
          label="Unread"
          checked={unreadOnly}
          onChange={(_event, data) => void controller.setFilters({ unreadOnly: data.checked })}
        />
        <Switch
          label="Flagged"
          checked={flaggedOnly}
          onChange={(_event, data) => void controller.setFilters({ flaggedOnly: data.checked })}
        />
      </div>

      {selected.length > 0 && (
        <div className={styles.bulk} role="toolbar" aria-label="Actions for selected conversations">
          <Text weight="semibold">{selected.length} selected</Text>
          <span className={styles.spacer} />
          <Button
            size="small"
            appearance="subtle"
            icon={<MailRead20Regular />}
            onClick={() => void controller.applyToSelection({ unread: false })}
          >
            Mark read
          </Button>
          <Button
            size="small"
            appearance="subtle"
            icon={<Flag20Regular />}
            onClick={() => void controller.applyToSelection({ flagged: true })}
          >
            Flag
          </Button>
          <BulkMove role="archive" label="Archive" icon={<Archive20Regular />} />
          <BulkMove role="trash" label="Delete" icon={<Delete20Regular />} />
        </div>
      )}

      {loading && threads.length === 0 ? (
        <div className={styles.skeleton} role="status" aria-label="Loading conversations">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div key={index} className={styles.skeletonRow}>
              <SkeletonItem size={12} />
              <SkeletonItem size={8} />
              <SkeletonItem size={8} />
            </div>
          ))}
        </div>
      ) : threads.length === 0 ? (
        <div className={styles.state} role="status">
          <Text weight="semibold">{emptyFolderMessage(folder, search !== '')}</Text>
          <Caption1>Nothing needs your attention here.</Caption1>
        </div>
      ) : (
        <ul className={styles.list} aria-label="Messages">
          {threads.map((thread) => (
            <Row
              key={thread.id}
              thread={thread}
              active={thread.id === activeThreadId}
              selected={selected.includes(thread.id)}
              accent={accent}
            />
          ))}
        </ul>
      )}

      {threads.length < total && (
        <div className={styles.footer}>
          <Caption1>
            {total - threads.length} more conversation{total - threads.length === 1 ? '' : 's'}
          </Caption1>
          <Button size="small" disabled={loading} onClick={() => void controller.loadMoreThreads()}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

function BulkMove(props: {
  role: 'archive' | 'trash';
  label: string;
  icon: ReactElement;
}): ReactElement | null {
  const controller = useController();
  const folder = useMail((s) => s.folders.find((f) => f.role === props.role));
  if (folder === undefined) return null;
  return (
    <Button
      size="small"
      appearance="subtle"
      icon={props.icon}
      onClick={() => void controller.applyToSelection({ folderId: folder.id })}
    >
      {props.label}
    </Button>
  );
}

function Row(props: {
  thread: Thread;
  active: boolean;
  selected: boolean;
  accent: string;
}): ReactElement {
  const styles = useStyles();
  const controller = useController();
  const { thread, active, selected, accent } = props;
  const archive = useMail((s) => s.folders.find((f) => f.role === 'archive'));
  const trash = useMail((s) => s.folders.find((f) => f.role === 'trash'));
  const subject = thread.subject || '(no subject)';

  return (
    <li
      className={mergeClasses(styles.row, active ? styles.active : undefined)}
      data-thread-id={thread.id}
      data-brand={thread.brandId}
      data-unread={thread.unread ? 'true' : 'false'}
    >
      {thread.unread && (
        <span className={styles.accent} style={{ background: accent }} aria-hidden="true" />
      )}
      <div className={styles.select}>
        <Checkbox
          aria-label={`Select ${subject}`}
          checked={selected}
          onChange={() => controller.toggleThreadSelection(thread.id)}
        />
      </div>

      {/* The row's controls sit beside the opening button, never inside it: a button
          within a button is unreachable by keyboard and by screen reader. */}
      <button
        type="button"
        className={styles.open}
        data-row-open="true"
        aria-current={active ? 'true' : undefined}
        aria-label={`${thread.unread ? 'Unread. ' : ''}${subject}, from ${participantLabel(thread)}`}
        onClick={() => void controller.openThread(thread.id)}
      >
        <span className={styles.line}>
          <Text
            className={mergeClasses(styles.sender, thread.unread ? styles.unread : undefined)}
            truncate
            wrap={false}
          >
            {participantLabel(thread)}
          </Text>
          <Text className={styles.date}>{relativeDate(thread.latestAt)}</Text>
        </span>
        <Text
          className={mergeClasses(styles.subject, thread.unread ? styles.unread : undefined)}
          truncate
          wrap={false}
        >
          {subject}
        </Text>
        <Text className={styles.preview} truncate wrap={false}>
          {thread.preview}
        </Text>
      </button>

      <div className={styles.indicators}>
        {thread.hasAttachments && <Attach16Filled aria-label="Has an attachment" />}
        {thread.flagged && <Flag16Filled aria-label="Flagged" />}
      </div>

      <div className={styles.actions} data-row-actions="true">
        <Button
          size="small"
          appearance="subtle"
          icon={thread.flagged ? <Flag20Filled /> : <Flag20Regular />}
          aria-label={thread.flagged ? `Unflag ${subject}` : `Flag ${subject}`}
          aria-pressed={thread.flagged}
          onClick={() => void controller.setThreadFlags(thread.id, { flagged: !thread.flagged })}
        />
        <Button
          size="small"
          appearance="subtle"
          icon={thread.unread ? <Mail20Regular /> : <MailRead20Regular />}
          aria-label={thread.unread ? `Mark ${subject} read` : `Mark ${subject} unread`}
          aria-pressed={!thread.unread}
          onClick={() => void controller.setThreadFlags(thread.id, { unread: !thread.unread })}
        />
        {archive !== undefined && (
          <Button
            size="small"
            appearance="subtle"
            icon={<Archive20Regular />}
            aria-label={`Archive ${subject}`}
            onClick={() => void controller.setThreadFlags(thread.id, { folderId: archive.id })}
          />
        )}
        {trash !== undefined && (
          <Button
            size="small"
            appearance="subtle"
            icon={<Delete20Regular />}
            aria-label={`Delete ${subject}`}
            onClick={() => void controller.setThreadFlags(thread.id, { folderId: trash.id })}
          />
        )}
      </div>
    </li>
  );
}
